import { CheckCircle2, ChevronDown, Clock3, Loader2, TerminalSquare, XCircle } from 'lucide-react';
import { useState } from 'react';

import { useMonkeysLocaleMessages } from '../../provider';
import { cn } from '../layout';
import type { AgentWorkbenchEventOf } from './activity-model';

export interface AgentWorkbenchToolClassNames {
  root?: string;
  trigger?: string;
  content?: string;
  input?: string;
  output?: string;
}

export interface AgentWorkbenchToolProps {
  event: AgentWorkbenchEventOf<'tool'>;
  expanded?: boolean;
  defaultExpanded?: boolean;
  className?: string;
  classNames?: AgentWorkbenchToolClassNames;
  onExpandedChange?: (expanded: boolean) => void;
}

function JsonValue({ value, className }: { value: unknown; className?: string }) {
  if (value === undefined) return null;
  const content = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return (
    <pre
      tabIndex={0}
      className={cn(
        'max-h-64 max-w-full overflow-auto overscroll-contain whitespace-pre-wrap break-words rounded-md bg-muted p-3 font-mono text-xs text-foreground [overflow-wrap:anywhere]',
        className,
      )}
    >
      {content}
    </pre>
  );
}

function ToolStatus({ status }: { status: AgentWorkbenchEventOf<'tool'>['payload']['status'] }) {
  const messages = useMonkeysLocaleMessages().agentWorkbench.tool.status;
  const commonClassName = 'size-3.5 shrink-0';

  if (status === 'pending') {
    return <Clock3 className={commonClassName} aria-hidden="true" />;
  }
  if (status === 'running') {
    return <Loader2 className={cn(commonClassName, 'animate-spin')} aria-hidden="true" />;
  }
  if (status === 'completed') {
    return <CheckCircle2 className={cn(commonClassName, 'text-primary')} aria-hidden="true" />;
  }
  return <XCircle className={cn(commonClassName, 'text-destructive-text')} aria-hidden="true" />;
}

export function AgentWorkbenchTool({
  event,
  expanded,
  defaultExpanded = event.payload.status === 'failed',
  className,
  classNames,
  onExpandedChange,
}: AgentWorkbenchToolProps) {
  const messages = useMonkeysLocaleMessages().agentWorkbench.tool;
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded);
  const isControlled = expanded !== undefined;
  const isExpanded = expanded ?? uncontrolledExpanded;
  const status = event.payload.status;
  const setExpanded = (nextExpanded: boolean) => {
    if (!isControlled) setUncontrolledExpanded(nextExpanded);
    onExpandedChange?.(nextExpanded);
  };

  return (
    <details
      open={isExpanded}
      className={cn(
        'group min-w-0 overflow-hidden rounded-lg border border-border bg-card text-card-foreground',
        classNames?.root,
        className,
      )}
      data-tool-status={status}
    >
      <summary
        className={cn(
          'flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden',
          classNames?.trigger,
        )}
        onClick={(event) => {
          event.preventDefault();
          setExpanded(!isExpanded);
        }}
      >
        <TerminalSquare className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate font-medium">{event.payload.toolName}</span>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-foreground">
          <ToolStatus status={status} />
          {messages.status[status]}
        </span>
        <ChevronDown
          className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>
      <div className={cn('min-w-0 space-y-3 border-t border-border px-3 py-3 text-sm', classNames?.content)}>
        {event.payload.input !== undefined ? (
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-muted-foreground">{messages.input}</div>
            <JsonValue value={event.payload.input} className={classNames?.input} />
          </div>
        ) : null}
        {event.payload.output !== undefined ? (
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-muted-foreground">{messages.output}</div>
            <JsonValue value={event.payload.output} className={classNames?.output} />
          </div>
        ) : null}
        {event.payload.error ? (
          <p
            tabIndex={0}
            className="max-h-32 overflow-auto whitespace-pre-wrap break-words text-sm text-destructive-text [overflow-wrap:anywhere]"
            role="alert"
          >
            {event.payload.error}
          </p>
        ) : null}
      </div>
    </details>
  );
}
